"use client";

import { useState } from "react";
import { HoverPrefetchLink } from "@/components/ui/hover-prefetch-link";
import { Card, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { formatCentsWithCurrency } from "@/lib/formatting/currency";
import {
  US_TILE_COLUMNS,
  US_TILE_POSITIONS,
  US_TILE_ROWS,
} from "@/lib/price-index/us-tile-grid";
import { cn } from "@/lib/utils";

export type PlacePrice = {
  key: string;
  name: string;
  code?: string;
  href: string;
  avgPriceCents: number | null;
  currency: "USD" | "CAD";
};

type PricedPlace = PlacePrice & { avgPriceCents: number };

const HEAT_STEPS = 5;

function isPriced(place: PlacePrice): place is PricedPlace {
  return place.avgPriceCents !== null;
}

function heatShare(priceCents: number, low: number, high: number): number {
  if (high <= low) return 0.4;
  const step = Math.min(
    HEAT_STEPS - 1,
    Math.floor(((priceCents - low) / (high - low)) * HEAT_STEPS),
  );
  return 0.12 + (step / (HEAT_STEPS - 1)) * 0.6;
}

function percentFrom(priceCents: number, referenceCents: number): number {
  return Math.round((priceCents / referenceCents - 1) * 100);
}

type Highlight = {
  highlightedKey: string | null;
  onHighlight: (key: string | null) => void;
};

function highlightHandlers(key: string, onHighlight: Highlight["onHighlight"]) {
  return {
    onMouseEnter: () => onHighlight(key),
    onMouseLeave: () => onHighlight(null),
    onFocus: () => onHighlight(key),
    onBlur: () => onHighlight(null),
  };
}

function Tile({
  place,
  share,
  isActive,
  onHighlight,
}: {
  place: PricedPlace;
  share: number;
  isActive: boolean;
  onHighlight: Highlight["onHighlight"];
}) {
  const [column, row] = US_TILE_POSITIONS[place.code ?? ""] ?? [0, 0];
  const price = formatCentsWithCurrency(place.avgPriceCents, place.currency);
  return (
    <HoverPrefetchLink
      href={place.href}
      aria-label={`${place.name}: ${price}`}
      {...highlightHandlers(place.key, onHighlight)}
      className={cn(
        "flex aspect-square flex-col items-center justify-center rounded-sm text-foreground transition-shadow",
        isActive && "ring-2 ring-foreground",
      )}
      style={{
        gridColumn: column + 1,
        gridRow: row + 1,
        backgroundColor: `color-mix(in oklab, var(--brand-accent) ${Math.round(share * 100)}%, var(--card))`,
      }}
    >
      <span className="text-[0.7rem] font-semibold leading-none">
        {place.code}
      </span>
      <span className="mt-0.5 hidden tabular-nums text-[0.6rem] leading-none sm:block">
        {price}
      </span>
    </HoverPrefetchLink>
  );
}

function TileMap({
  places,
  currentKey,
  highlightedKey,
  onHighlight,
}: Highlight & { places: PricedPlace[]; currentKey?: string }) {
  const usPlaces = places.filter(
    (place) => place.code && US_TILE_POSITIONS[place.code],
  );
  if (usPlaces.length === 0) return null;
  const prices = usPlaces.map((place) => place.avgPriceCents);
  const low = Math.min(...prices);
  const high = Math.max(...prices);
  return (
    <div
      className="grid gap-1"
      style={{
        gridTemplateColumns: `repeat(${US_TILE_COLUMNS}, minmax(0, 1fr))`,
        gridTemplateRows: `repeat(${US_TILE_ROWS}, auto)`,
      }}
    >
      {usPlaces.map((place) => (
        <Tile
          key={place.key}
          place={place}
          share={heatShare(place.avgPriceCents, low, high)}
          isActive={place.key === currentKey || place.key === highlightedKey}
          onHighlight={onHighlight}
        />
      ))}
    </div>
  );
}

function differenceLabel(
  place: PlacePrice,
  referenceCents: number | null,
): string {
  if (place.avgPriceCents === null || !referenceCents) return "";
  if (place.currency !== "USD") return "";
  const percent = percentFrom(place.avgPriceCents, referenceCents);
  return `${percent > 0 ? "+" : ""}${percent}%`;
}

function RankedRow({
  place,
  referenceCents,
  isHighlighted,
  onHighlight,
}: {
  place: PlacePrice;
  referenceCents: number | null;
  isHighlighted: boolean;
  onHighlight: Highlight["onHighlight"];
}) {
  return (
    <li>
      <HoverPrefetchLink
        href={place.href}
        {...highlightHandlers(place.key, onHighlight)}
        className={cn(
          "grid grid-cols-[minmax(0,1fr)_auto_3.5rem] items-baseline gap-3 border-b border-border-subtle px-1 py-2 text-sm text-foreground hover:bg-muted",
          isHighlighted && "bg-muted",
        )}
      >
        <span className="truncate">{place.name}</span>
        <span className="tabular-nums">
          {place.avgPriceCents === null
            ? ""
            : formatCentsWithCurrency(place.avgPriceCents, place.currency)}
        </span>
        <span className="text-right tabular-nums text-xs text-fg-muted">
          {differenceLabel(place, referenceCents)}
        </span>
      </HoverPrefetchLink>
    </li>
  );
}

function rankPlaces(places: PlacePrice[], search: string): PlacePrice[] {
  const ranked = [
    ...places.filter(isPriced).sort((a, b) => a.avgPriceCents - b.avgPriceCents),
    ...places.filter((place) => !isPriced(place)),
  ];
  const query = search.trim().toLowerCase();
  return query
    ? ranked.filter((place) => place.name.toLowerCase().includes(query))
    : ranked;
}

function RankedList({
  places,
  referenceCents,
  highlightedKey,
  onHighlight,
}: Highlight & { places: PlacePrice[]; referenceCents: number | null }) {
  const [search, setSearch] = useState("");
  return (
    <div className="flex min-h-0 flex-col gap-2">
      <Input
        type="search"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        placeholder="Find a place"
        aria-label="Find a place"
      />
      <ol className="max-h-96 overflow-y-auto border-t border-border-subtle">
        {rankPlaces(places, search).map((place) => (
          <RankedRow
            key={place.key}
            place={place}
            referenceCents={referenceCents}
            isHighlighted={place.key === highlightedKey}
            onHighlight={onHighlight}
          />
        ))}
      </ol>
    </div>
  );
}

export function PlaceExplorer({
  heading,
  places,
  referenceCents,
  showMap,
  currentKey,
}: {
  heading: string;
  places: PlacePrice[];
  referenceCents: number | null;
  showMap: boolean;
  currentKey?: string;
}) {
  const [highlightedKey, setHighlightedKey] = useState<string | null>(null);
  if (places.length === 0) return null;
  const priced = places.filter(isPriced);
  return (
    <Card>
      <CardHeader>
        <CardTitle>{heading}</CardTitle>
      </CardHeader>
      <div
        className={cn(
          "grid gap-6",
          showMap && "lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]",
        )}
      >
        {showMap && (
          <TileMap
            places={priced}
            currentKey={currentKey}
            highlightedKey={highlightedKey}
            onHighlight={setHighlightedKey}
          />
        )}
        <RankedList
          places={places}
          referenceCents={referenceCents}
          highlightedKey={highlightedKey}
          onHighlight={setHighlightedKey}
        />
      </div>
    </Card>
  );
}
